import { ColumnSeries, DateAxis, ValueAxis, XYChart } from "@amcharts/amcharts4/charts";
import { color, create, percent } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component } from "react";
import WebFont from "webfontloader";
import { BLACK, WHITE } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { columnSeriesConfiguration, configureDateAxis, configureValueAxis } from "../helpers/configurations";
import { legendConfigurationOCE, oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  theme: ThemeInt;
  chartName: string;
  isLoading: boolean;
  seriesList: Array<{ name: string; field: string }>;
  colorMapping: Record<string, string>;
}

interface ISeries {
  name: string;
  field: string;
}

class MsrpDisByModelAndMonthChart extends Component<Props> {
  chartId = this.props.chartName;
  chart!: XYChart;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (!_.isEqual(prevProps, this.props)) {
      this.chart.dispose();
      this.initChart();
    }
  }

  componentWillUnmount() {
    this.chart.dispose();
  }

  initChart() {
    const { data, theme, seriesList, colorMapping } = this.props;

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = data;
    this.chart.fontSize = "var(--filter_font)";
    this.chart.numberFormatter.numberFormat = "#.##";

    // Creates Legend
    this.chart.legend = legendConfigurationOCE(theme);
    this.chart.legend.reverseOrder = false;
    this.chart.legend.position = "bottom";
    this.chart.legend.reverseOrder = true;

    // Create current period date chart axes
    const dateAxis = this.chart.xAxes.push(new DateAxis());
    configureDateAxis(dateAxis, theme);
    dateAxis.renderer.minGridDistance = 10;
    dateAxis.dateFormats.setKey("month", "MMMM yy");
    dateAxis.periodChangeDateFormats.setKey("month", "MMM yy");

    const valueAxis = this.chart.yAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    valueAxis.min = 0;
    valueAxis.max = 100;
    valueAxis.renderer.labels.template.adapter.add("text", function (text) {
      return text + "%";
    });

    seriesList?.map(({ name, field }: ISeries) => {
      return this.createSeries({
        name: name,
        field: field,
      });
    });
  }

  createSeries({ name, field }: ISeries) {
    const series = this.chart.series.push(new ColumnSeries());
    columnSeriesConfiguration(series, {
      name: name,
      valueY: field,
    });
    series.stacked = true;
    series.dataFields.dateX = "date";
    series.columns.template.width = percent(75);
    series.columns.template.tooltipText = "{name}: {valueY.formatNumber('#.#')}%[/]";
    series.columns.template.fill = color(this.props.colorMapping[name]);
    series.columns.template.stroke = color(this.props.colorMapping[name]);
    if (series.tooltip) {
      series.tooltip.getFillFromObject = false;
      series.tooltip.autoTextColor = false;
      series.tooltip.background.fill = color(WHITE);
      series.tooltip.label.fill = color(BLACK);
    }
    series.columns.template.adapter.add("tooltipText", (value, target) => {
      if (target?.tooltipDataItem) {
        const dataValue = target?.tooltipDataItem?.dataContext as { date: string; [index: string]: string | number };
        // @ts-ignore
        const name = target.tooltipDataItem.component?.dataFields?.valueY;

        let text = `${name.split("_")[1]}: ${dataValue[name]}%\n`;

        text += `Sales volume: ${dataValue[name + "_volume"]}`;

        return text;
      }
    });
  }

  render() {
    return (
      <div className={"chartWrapper"}>
        <div id={this.props.chartName} className={"graph"} />
        <LoadingEllipsis isLoading={this.props.isLoading} />
      </div>
    );
  }
}

export default MsrpDisByModelAndMonthChart;
